import {createContext, useContext, useMemo} from 'react'
import utils from '@eitje/utils'
import {useFloatingTree} from '@floating-ui/react'
import _ from 'lodash'
import {SearchProvider, useSearchContext} from '../../../search_input'
import {normalizeItems, validateMinMaxItems} from './helpers'
import {useExpandedGroups, usePreProcessValue} from './hooks'

const SelectContext = createContext()
const {Provider} = SelectContext

const Inner = ({
	value,
	onChange,
	items: originalItems,
	type = 'single', // single | multiple
	valueField = 'id',
	labelField = 'name',
	groupProps,
	minItems,
	maxItems,
	showToggleAll,
	showClearAll, // boolean | object (to allow overwriting default showAtCount and clear all t))
	setIsOpen = _.noop, // setIsOpen is only defined in floating selects
	closeOnSelect = true,
	ItemComponent,
	itemHeight,
	virtualized,
	readOnly,
	children,
}) => {
	const {filteredItems} = useSearchContext()
	const tree = useFloatingTree()

	const single = type === 'single'
	const multiple = type === 'multiple'
	value = usePreProcessValue({items: originalItems, value, valueField, multiple})

	// create a flat array of items with type 'group', 'item', 'nested-group' and 'nested-item'
	const items = useMemo(() => normalizeItems({items: filteredItems, groupProps, labelField, valueField}), [filteredItems])

	// keep track of which groups are expanded and toggle them on click
	const {expandedGroups, toggleGroupExpansion} = useExpandedGroups({items, groupProps})

	const updateValue = data => {
		if (readOnly) return
		const items = utils.alwaysArray(data)
		// disabled items cannot be updated (e.g. selected or deselected)
		const newValues = items.filter(i => !i.disabled)._map('value')

		if (single) {
			onChange(newValues[0])
			if (closeOnSelect) {
				setIsOpen(false)
				tree?.events.emit('click')
			}
			return
		}

		const newCount = newValues.length
		if (newCount === 0 && validateMinMaxItems(newCount, {minItems, maxItems})) {
			onChange([]) // reset values
			return
		}

		const allSelected = newValues.every(val => value.includes(val))
		// if all items are selected, deselect them all, otherwise add them to the current values
		const values = allSelected ? value.filter(val => !newValues.includes(val)) : _.uniq([...value, ...newValues])
		if (validateMinMaxItems(values.length, {minItems, maxItems})) {
			onChange(values)
		}
	}

	const getSelectedItems = items => {
		return items.filter(item => (single ? value === item.value : value?.includes(item.value)))
	}

	const selectedItemIndex = single && value ? items.findIndex(i => i.type !== 'nested-item' && i.data?.value === value) : null
	const selectedIndex = selectedItemIndex === -1 ? null : selectedItemIndex

	const providerValue = {
		items,
		updateValue,
		type,
		single,
		multiple,
		value,
		selectedIndex,
		groupProps,
		showToggleAll: multiple && showToggleAll,
		showClearAll: multiple && showClearAll,
		getSelectedItems,
		expandedGroups,
		toggleGroupExpansion,
		ItemComponent,
		itemHeight,
		virtualized,
		readOnly,
	}

	return <Provider value={providerValue}>{children}</Provider>
}

export const SelectProvider = ({items, labelField = 'name', search, ...rest}) => {
	const searchConfig = {
		showSearch: items.length > 10,
		searchEmptyProps: {size: 'small'},
		color: 'black',
		searchField: labelField,
		...search,
	}

	const virtualized = items.length > 20

	return (
		<SearchProvider items={items} options={searchConfig}>
			<Inner items={items} labelField={labelField} virtualized={virtualized} {...rest} />
		</SearchProvider>
	)
}

export const useSelectContext = () => useContext(SelectContext)
